import { $el, whenDefined } from 'src/scripts/utils/utils'
import type { Nullable } from '../../types/common'
import { BaseComponent } from 'src/scripts/components/base-component'
import { isMobile } from 'src/scripts/utils/check-media'
import { ProductModal, VariantPicker } from '.'
import {
  CarouselComponent,
  CarouselDots,
} from 'src/scripts/components/carousel'
import { Variant } from 'src/scripts/types/shopify'

export class ProductContent extends BaseComponent {
  variantPickerSelector: string

  containerAttribute: string
  visibleClass: string
  mobileClass: string

  variantPicker: VariantPicker
  variant: Variant

  desktopContent: HTMLElement
  mobileContent: HTMLElement
  desktopContainers: HTMLElement[]
  mobileContainers: HTMLElement[]

  carousels: CarouselComponent[]
  dots: CarouselDots[]
  modal: Nullable<ProductModal>
  initialImagesQuantity: number | null
  currentImagesQuantity: number | null
  modalQuantityElement: Nullable<HTMLElement>

  isMobile: boolean

  carouselImages: HTMLElement[]
  dotsImages: HTMLElement[]
  gridImages: HTMLElement[]
  modalImages: HTMLElement[]

  desktopImages: any
  mobileImages: any
  currentContainers: any
  currentImages: any
  currentDots: CarouselDots

  resizeObserver: ResizeObserver

  constructor() {
    super()

    this.variantPickerSelector = 'variant-picker'
    this.containerAttribute = 'data-container'
    this.visibleClass = 'product__image-visible'

    this.isMobile = isMobile()

    this.initialImagesQuantity = null
    this.currentImagesQuantity = null

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)
  }

  connectedCallback(): void {
    this.init()
  }

  disconnectedCallback(): void {
    // 快速查看可能在变体选择器初始化前卸载，此时无需解绑事件。
    this.variantPicker?.off('change', this.handleVariantChange)
  }

  init = (): void => {
    this.setMediaScope()
    Promise.all([whenDefined('product-modal'), whenDefined('variant-picker')])
      .then(() => {
        this.initializeMediaScope()
      })
      .catch(error => {
        console.log(error)
      })
  }

  setMediaScope = (): void => {
    const scope = this

    this.variantPicker = this.querySelector(this.variantPickerSelector)
    this.desktopContent = scope.querySelector('[data-product-desktop-media]')
    this.mobileContent = scope.querySelector('[data-product-mobile-media]')
    this.desktopContainers = [
      ...this.desktopContent.querySelectorAll(`[${this.containerAttribute}]`),
    ] as HTMLElement[]
    this.mobileContainers = [
      ...this.mobileContent.querySelectorAll(`[${this.containerAttribute}]`),
    ] as HTMLElement[]
    this.dots = [...scope.querySelectorAll('carousel-dots')] as CarouselDots[]
    this.carousels = [
      ...scope.querySelectorAll('carousel-component, carousel-dots'),
    ] as CarouselComponent[]
    this.modal =
      scope.querySelector('product-modal') ??
      this.parentElement.querySelector('product-modal')
  }

  initializeMediaScope = (): void => {
    if (this.modal?.element) {
      const container = $el(
        `[${this.containerAttribute}]`,
        this.modal.element,
      )

      this.mobileContainers.push(container)
      this.desktopContainers.push(container)
      this.modalQuantityElement = this.modal.element.querySelector(
        `[data-product-modal-slides-total]`,
      )
    }

    this.desktopImages = this.assignImages(this.desktopContainers)
    this.mobileImages = this.assignImages(this.mobileContainers)
    this.setCurrentContainers()
    this.setInitialImagesLength()
    this.handleVariantChange({ variant: this.variantPicker.variant })
    this.variantPicker.on('change', this.handleVariantChange)

    const counterTotal = this.desktopContent.querySelector(
      '.media-counter-thumb .media-counter-total',
    )
    const counterTotalMb = this.mobileContent.querySelector(
      '.media-counter-dot .media-counter-total',
    )
    if (counterTotal) counterTotal.textContent = String(this.currentImagesQuantity)
    if (counterTotalMb)
      counterTotalMb.textContent = String(this.currentImagesQuantity)
  }

  handleVariantChange = ({ variant }): void => {
    this.currentContainers.forEach(container => {
      switch (container.dataset.container) {
        case 'carousel':
          this.replaceCarouselSlides(container)
          break
        case 'dots':
          this.replaceDotsSlides(container)
          break
        case 'grid':
          this.replaceGridSlides(container)
          break
        case 'modal':
          this.replaceModalSlides(container)
          break
        default:
          break
      }
    })

    this.reInitCarousels()

    this.scrollToVariant(variant)

    const nomad2xNewPdp = document.querySelector('body.nomad-2x-new-pdp')
    if (
      nomad2xNewPdp &&
      variant.name.indexOf('Velotric Nomad 2X') !== -1
    ) {
      const contentDivs = document.querySelectorAll('[data-show]')
      const isMulticam = variant.option2 === 'Multi-Terrain Camo'

      document.querySelectorAll('.multicam_bundle').forEach(element => {
        element.classList.toggle('hidden', !isMulticam)
      })
      document.querySelectorAll('.origin_bundle').forEach(element => {
        element.classList.toggle('hidden', isMulticam)
      })
      contentDivs.forEach(element => {
        const expectedValue = isMulticam ? 'Multicam' : 'Other'
        element.classList.toggle(
          'hidden',
          element.getAttribute('data-show') !== expectedValue,
        )
      })
    }

    if (this.querySelectorAll('.rider-heights').length > 0) {
      this.variantPicker.setAttribute('data-style-value', variant.option1)
      if (document.querySelector('.n2_style')) {
        this.variantPicker.setAttribute('data-size-value', variant.option2)
        document.querySelectorAll<HTMLElement>('.n2_style')[0].textContent =
          variant.option1
      }
      document.querySelectorAll('.rider-heights').forEach(element => {
        element.classList.toggle(
          'selected',
          element.getAttribute('data-value') == variant.option1 ||
            element.getAttribute('data-value') == variant.id,
        )
      })
    }

    if (this.modal) {
      this.modal.updateModal()
    }

    this.variant = variant

    const preorderElement = document.getElementById('preorder-message')
    const preorderData = preorderElement?.textContent
      ? JSON.parse(preorderElement.textContent)
      : null
    if (preorderData) {
      const variantName = this.variant.options.join('/')
      const message = preorderData[variantName]?.message || ''
      const preorderWrap = document.querySelector('.pre-order-wrap')
      const preorderMessage = preorderWrap.querySelector('.pre-order-msg')

      preorderMessage.classList.toggle('active', Boolean(message))
      preorderMessage.textContent = message
    }
  }

  replaceCarouselSlides = (container: HTMLElement): void => {
    const carouselImages = this.getImages(this.currentImages['carouselImages'])

    carouselImages[0].classList.add('is-selected')
    carouselImages[0].classList.toggle(
      'product-media-carousel-mobile__slide--alone',
      carouselImages.length < 2,
    )

    this.pasteImages(container, carouselImages)
  }

  replaceDotsSlides = (container: HTMLElement): void => {
    const dotsImages = this.getImages(this.currentImages['dotsImages'])

    dotsImages.forEach((img, i) => {
      img.setAttribute('data-dot-index', String(i))
    })

    this.pasteImages(container, dotsImages)
  }

  replaceGridSlides = (container: HTMLElement): void => {
    const gridImages = this.getImages(this.currentImages['gridImages'])

    this.pasteImages(container, gridImages)
  }

  replaceModalSlides = (container: HTMLElement): void => {
    if (this.modal) {
      const modalImages = this.getImages(this.currentImages['modalImages'])

      this.pasteImages(container, modalImages)

      this.modalQuantityElement.textContent = String(modalImages.length)
    }
  }

  getImages = (initialImages: HTMLElement[]): HTMLElement[] => {
    const images = initialImages.map(el => el.cloneNode(true)) as HTMLElement[]
    const availableImages = images.filter(img => img.dataset.mediaAb !== 'a')
    const filtered = availableImages.filter(img => {
      const imgVariant = (img.dataset.variant || '')
        .replace(/\s*\[poi:[^\]]+\]/g, '')
        .replace(/\s*\[media-ab:[^\]]+\]/g, '')
        .trim()

      if (imgVariant.includes('_ads')) {
        return false
      }

      return imgVariant.includes(String(this.variantPicker.variant.id))
    })

    return filtered.length > 0 ? filtered : availableImages
  }

  pasteImages = (container: HTMLElement, images: HTMLElement[]): void => {
    container.replaceChildren(...images)

    this.currentImagesQuantity = images.length
  }

  reInitCarousels = (): void => {
    if (this.carousels.length > 0) {
      this.carousels.forEach(carousel => {
        carousel.reInit()
      })
    }
  }

  assignImages = (containers: HTMLElement[]): any => {
    const carouselImages = []
    const dotsImages = []
    const gridImages = []
    const modalImages = []

    containers.forEach(container => {
      const clone = container.cloneNode(true) as HTMLElement
      const children = [...clone.children]

      switch (container.dataset.container) {
        case 'carousel':
          carouselImages.push(...children)
          carouselImages.forEach((img, i) => {
            img.classList.remove('is-selected')
          })
          break
        case 'dots':
          dotsImages.push(...children)
          dotsImages.forEach((img, i) => {
            img.classList.remove('is-primary')
            img.classList.remove('is-selected')
            img.classList.remove('is-in-view')
            img.classList.remove('is-snapped')
            img.classList.remove('is-prev-prev')
          })
          break
        case 'grid':
          gridImages.push(...children)
          break
        case 'modal':
          modalImages.push(...children)
          break
        default:
          break
      }
    })

    return { carouselImages, dotsImages, gridImages, modalImages }
  }

  scrollToVariant = (variant): void => {
    if (
      this.currentImagesQuantity === this.initialImagesQuantity &&
      variant.featured_media
    ) {
      this.variantPicker.setFirstMediaByVariant(variant)
    } else {
      this.carousels.forEach(carousel => {
        carousel.embla.scrollTo(0)
      })

      this.variantPicker.setFirstMediaByVariant(0)
    }

    if (this.currentDots) {
      const dots = [...this.currentDots.embla.slideNodes()]
      const selectedDotIndex =
        this.currentDots.carousel.embla.selectedScrollSnap()

      dots.forEach(dot => {
        const isSelectedDot = +dot.dataset.dotIndex === selectedDotIndex
        dot.classList.toggle('is-primary', isSelectedDot)
      })

      const counterScope =
        this.currentDots.closest('div[data-product-media-container]') || this
      const counterCurrent = counterScope.querySelector(
        '.media-counter-thumb .media-counter-current',
      )
      if (counterCurrent) {
        counterCurrent.textContent = String(selectedDotIndex + 1)
        const counterTotal = counterScope.querySelector(
          '.media-counter-thumb .media-counter-total',
        )
        if (counterTotal) counterTotal.textContent = String(dots.length || 1)
      }
      const counterCurrentMb = counterScope.querySelector(
        '.media-counter-dot .media-counter-current',
      )
      if (counterCurrentMb) {
        counterCurrentMb.textContent = String(selectedDotIndex + 1)
        const counterTotalMb = counterScope.querySelector(
          '.media-counter-dot .media-counter-total',
        )
        if (counterTotalMb)
          counterTotalMb.textContent = String(dots.length || 1)
      }
    }
  }

  setCurrentContainers = (): void => {
    this.currentContainers = this.isMobile
      ? this.mobileContainers
      : this.desktopContainers

    this.currentImages = this.isMobile ? this.mobileImages : this.desktopImages
    this.currentDots = this.dots.find(el => el.offsetWidth > 0)
  }

  setInitialImagesLength = (): void => {
    const isCarouselImages = !!this.currentImages.carouselImages?.length

    this.initialImagesQuantity = isCarouselImages
      ? this.currentImages.carouselImages?.length
      : this.currentImages.gridImages?.length
  }

  handleResize = (): void => {
    if (this.isMobile !== isMobile()) {
      this.isMobile = isMobile()

      this.setCurrentContainers()
    }
  }
}
